import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const reportTypeMeta = {
  Daily: {
    accent: 'from-emerald-400/80 to-emerald-500/80',
    description: 'Receive insights every morning to stay ahead of critical shifts.',
  },
  Weekly: {
    accent: 'from-sky-400/80 to-indigo-500/80',
    description: 'Summarise trends, anomalies and recommendations every week.',
  },
  Monthly: {
    accent: 'from-violet-400/80 to-rose-500/80',
    description: 'High-level performance reports for leadership and strategic planning.',
  },
};

const shimmerVariant = {
  animate: {
    backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
    transition: { duration: 8, repeat: Infinity, ease: 'linear' },
  },
};

function NewReport() {
  const user =
    localStorage.getItem('username') ||
    sessionStorage.getItem('username') ||
    'demo';
  const [devices, setDevices] = useState([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(true);
  const [form, setForm] = useState({
    report_name: '',
    device: '',
    unit: '',
    report_type: 'Daily',
    scheduled_time: '',
    assigned_to: [],
  });
  const [emailInput, setEmailInput] = useState('');
  const [toast, setToast] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const res = await fetch(`/get_user_devices.php?username=${encodeURIComponent(user)}`);
        const data = await res.json();
        setDevices(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error(error);
        setDevices([]);
      } finally {
        setIsLoadingDevices(false);
      }
    };

    fetchDevices();
  }, [user]);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 3500);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const updateField = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const selectDevice = (e) => {
    const deviceName = e.target.value;
    const deviceObj = devices.find((d) => d.device_name === deviceName);
    setForm((prev) => ({
      ...prev,
      device: deviceName,
      unit: deviceObj ? deviceObj.unit : '',
    }));
  };

  const addEmail = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = emailInput.trim();
      if (!value) return;
      if (form.assigned_to.includes(value)) {
        setToast({ message: 'Recipient already added.', success: false });
        return;
      }
      setForm((prev) => ({
        ...prev,
        assigned_to: [...prev.assigned_to, value],
      }));
      setEmailInput('');
    }
  };

  const removeEmail = (index) => {
    setForm((prev) => ({
      ...prev,
      assigned_to: prev.assigned_to.filter((_, i) => i !== index),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    const payload = {
      ...form,
      username: user,
      assigned_to: form.assigned_to.join(','),
    };
    try {
      const res = await fetch('/add_report.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setToast({ message: 'Report scheduled successfully.', success: true });
        setForm({
          report_name: '',
          device: '',
          unit: '',
          report_type: 'Daily',
          scheduled_time: '',
          assigned_to: [],
        });
      } else {
        throw new Error(data.message || 'Failed to add report');
      }
    } catch (err) {
      console.error(err);
      setToast({ message: 'Unable to create report. Please try again.', success: false });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden text-white">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-[#151C2A] via-[#0C1017] to-[#080B10]" />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 0.45, scale: 1 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="absolute left-10 top-16 h-72 w-72 rounded-full bg-primary/35 blur-3xl"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 0.35, scale: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut', delay: 0.15 }}
          className="absolute bottom-10 right-6 h-80 w-80 rounded-full bg-sky-500/30 blur-3xl"
        />
      </div>

      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed right-6 top-6 z-50 flex items-center gap-3 rounded-full border px-5 py-3 text-sm shadow-xl ${
              toast.success
                ? 'border-emerald-500/60 bg-emerald-500/15 text-emerald-200'
                : 'border-rose-500/60 bg-rose-500/15 text-rose-200'
            }`}
          >
            <span className="text-lg">{toast.success ? '✅' : '⚠️'}</span>
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="relative mx-auto flex w-full max-w-6xl flex-col gap-10 px-6 py-16"
      >
        <header className="flex flex-col gap-3">
          <p className="text-xs uppercase tracking-[0.4em] text-white/60">Reports</p>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="text-4xl font-semibold">Create automated reports</h1>
              <p className="max-w-xl text-sm text-white/65">
                Configure the cadence, recipients and dataset for your next report. Everything can be adjusted later in
                Current Reports.
              </p>
            </div>
            <div className="flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.3em] text-white/60">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/70" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-300" />
              </span>
              Real-time sync
            </div>
          </div>
        </header>

        <div className="mx-auto w-full max-w-4xl">
          <form onSubmit={submit} className="space-y-8">
            <section className="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold">Report details</h2>
                  <p className="text-sm text-white/60">Choose the dataset and give this report a memorable name.</p>
                </div>
                <motion.span
                  className="hidden h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/10 text-sm font-semibold uppercase tracking-wide text-white/70 md:flex"
                  animate={{ rotate: [0, 6, -4, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                >
                  01
                </motion.span>
              </div>
              <div className="mt-6 grid gap-5 md:grid-cols-2">
                <label className="flex flex-col gap-2 text-sm">
                  <span className="text-white/70">Report name</span>
                  <input
                    type="text"
                    name="report_name"
                    value={form.report_name}
                    onChange={updateField}
                    required
                    placeholder="E.g. Pump motor health"
                    className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-base text-white placeholder:text-white/40 focus:border-primary/60 focus:outline-none focus:ring-0"
                  />
                </label>
                <label className="flex flex-col gap-2 text-sm">
                  <span className="text-white/70">Account</span>
                  <input
                    type="text"
                    value={user}
                    disabled
                    className="w-full rounded-2xl border border-white/5 bg-white/5 px-4 py-3 text-base text-white/70"
                  />
                </label>
              </div>
              <div className="mt-6 grid gap-5 md:grid-cols-2">
                <label className="flex flex-col gap-2 text-sm">
                  <span className="text-white/70">Device</span>
                  <select
                    name="device"
                    value={form.device}
                    onChange={selectDevice}
                    required
                    disabled={isLoadingDevices}
                    className="w-full appearance-none rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-base text-white focus:border-primary/60 focus:outline-none"
                  >
                    <option value="">{isLoadingDevices ? 'Loading devices…' : 'Select device'}</option>
                    {devices.map((d) => (
                      <option key={d.device_name} value={d.device_name} className="bg-[#0C1017] text-white">
                        {d.device_name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-2 text-sm">
                  <span className="text-white/70">Unit (auto)</span>
                  <input
                    type="text"
                    value={form.unit}
                    readOnly
                    placeholder="Select a device first"
                    className="w-full rounded-2xl border border-dashed border-white/10 bg-white/5 px-4 py-3 text-base text-white/70"
                  />
                </label>
              </div>
            </section>

            <section className="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold">Schedule & delivery</h2>
                  <p className="text-sm text-white/60">Set the cadence and recipients for this automated report.</p>
                </div>
                <motion.span
                  className="hidden h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/10 text-sm font-semibold uppercase tracking-wide text-white/70 md:flex"
                  animate={{ rotate: [0, -4, 6, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
                >
                  02
                </motion.span>
              </div>

              <div className="mt-6 grid gap-5">
                <div className="flex flex-wrap items-center gap-3">
                  {['Daily', 'Weekly', 'Monthly'].map((type) => {
                    const active = form.report_type === type;
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setForm((prev) => ({ ...prev, report_type: type }))}
                        className={`group relative overflow-hidden rounded-full border px-4 py-2 text-sm font-semibold transition-all duration-300 ${
                          active
                            ? 'border-transparent bg-gradient-to-r from-primary/80 to-sky-500/70 text-white shadow-lg shadow-primary/20'
                            : 'border-white/10 bg-white/5 text-white/70 hover:border-primary/40 hover:text-white'
                        }`}
                      >
                        <span className="relative z-10">{type}</span>
                        {active && (
                          <motion.span
                            layoutId="reportTypeGlow"
                            className={`absolute inset-0 -z-0 bg-gradient-to-r ${reportTypeMeta[type].accent} opacity-90`}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>

                <div className="grid gap-5 md:grid-cols-2">
                  <label className="flex flex-col gap-2 text-sm">
                    <span className="text-white/70">Scheduled time</span>
                    <input
                      type="time"
                      name="scheduled_time"
                      value={form.scheduled_time}
                      onChange={updateField}
                      required
                      className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-base text-white placeholder:text-white/40 focus:border-primary/60 focus:outline-none"
                    />
                  </label>
                  <div className="rounded-2xl border border-dashed border-white/15 bg-white/5 px-4 py-3 text-sm text-white/65">
                    {reportTypeMeta[form.report_type].description}
                  </div>
                </div>

                <div>
                  <div className="flex flex-wrap gap-2">
                    {form.assigned_to.map((email, idx) => (
                      <motion.span
                        key={email}
                        layout
                        className="group flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs text-white/80 backdrop-blur"
                      >
                        {email}
                        <button
                          type="button"
                          onClick={() => removeEmail(idx)}
                          className="rounded-full bg-white/10 px-2 py-0.5 text-white/60 transition hover:bg-rose-500/40 hover:text-white"
                        >
                          ×
                        </button>
                      </motion.span>
                    ))}
                  </div>
                  <label className="mt-3 flex flex-col gap-2 text-sm">
                    <span className="text-white/70">Assign to</span>
                    <input
                      type="email"
                      placeholder="Press enter to add each recipient"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      onKeyDown={addEmail}
                      className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-base text-white placeholder:text-white/40 focus:border-primary/60 focus:outline-none"
                    />
                  </label>
                </div>
              </div>
            </section>

            <div className="flex justify-end">
              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                whileTap={{ scale: isSubmitting ? 1 : 0.97 }}
                className={`group relative inline-flex items-center justify-center gap-3 overflow-hidden rounded-full px-7 py-3 text-sm font-semibold uppercase tracking-[0.3em] transition ${
                  isSubmitting
                    ? 'cursor-not-allowed border border-white/10 bg-white/10 text-white/60'
                    : 'border border-transparent bg-gradient-to-r from-[#bf1d2e] via-[#d83b4b] to-[#f97316] text-white shadow-lg shadow-[#bf1d2e]/30'
                }`}
                aria-label="Generate report"
              >
                <span className="relative z-10">
                  {isSubmitting ? 'Scheduling…' : 'Generate report'}
                </span>
                {!isSubmitting && (
                  <motion.span
                    className="relative z-10 flex h-2.5 w-2.5 items-center justify-center"
                    animate={{ opacity: [0.6, 1, 0.6], x: [0, 4, 0] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="h-3 w-3 text-white"
                    >
                      <path d="M7.5 4.5l5 5-5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                    </svg>
                  </motion.span>
                )}
                <motion.span
                  variants={shimmerVariant}
                  animate="animate"
                  className="absolute inset-0 -z-0 bg-[linear-gradient(120deg,rgba(255,255,255,0.08)_0%,rgba(255,255,255,0.18)_45%,rgba(255,255,255,0.08)_100%)] bg-[length:300%_100%] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                />
              </motion.button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
}

export default NewReport;
